import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { AIUsageSummary, AIUsageRow } from '../../types';
import { Modal } from '../../components/common/Modal';
import {
  Sparkles, RefreshCw, Loader2, Search, Gauge, DollarSign,
  MessageSquare, Ban, SlidersHorizontal, RotateCcw, Settings2,
} from 'lucide-react';

// Iris (ask-your-footage) usage monitoring. Shows what the platform spent this
// month and, per customer: their plan, the plan's credit allowance, how much
// they've used, cost, and when they last asked — plus controls to suspend,
// raise, or reset a customer's limit. Credits = answered questions. A per-
// customer override (ai_limits) wins over the plan allowance; clearing it falls
// back to the plan.
export const AIUsagePage: React.FC = () => {
  const [data, setData] = useState<AIUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = usePersistentState('aiUsage.search', '');
  const [selected, setSelected] = useState<AIUsageRow | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAIUsage());
    } catch (e) {
      setError((e as { message?: string })?.message || 'Could not load AI usage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Effective monthly limit a customer is held to: the override if set, else the
  // plan allowance. -1 = unlimited, 0 = suspended/off, null = unresolved.
  const effectiveLimit = (r: AIUsageRow): number | null =>
    r.monthlyCap != null ? r.monthlyCap : r.planCredits;

  // Apply a limit change, then refresh and keep the modal in sync.
  const applyLimit = async (row: AIUsageRow, cap: number | null) => {
    setBusy(true);
    try {
      await api.setAILimit(row.userId, cap);
      const fresh = await api.getAIUsage();
      setData(fresh);
      setSelected(fresh.byUser.find((x) => x.userId === row.userId) ?? { ...row, monthlyCap: cap });
    } catch (e) {
      window.alert((e as { message?: string })?.message || 'Could not update the limit.');
    } finally {
      setBusy(false);
    }
  };

  const suspend = (row: AIUsageRow) => {
    if (!window.confirm(`Suspend Iris for ${row.name || row.email}? They won't be able to ask any questions until you lift it.`)) return;
    applyLimit(row, 0);
  };

  const setCustomLimit = (row: AIUsageRow) => {
    const cur = row.monthlyCap != null ? String(row.monthlyCap) : (row.planCredits != null ? String(row.planCredits) : '');
    const input = window.prompt(
      `Custom monthly Iris credits for ${row.name || row.email}.\n` +
      `This overrides the plan allowance. Enter a number, −1 for unlimited, 0 to suspend.`,
      cur,
    );
    if (input === null) return;
    const n = Math.floor(Number(input.trim()));
    if (Number.isNaN(n)) { window.alert('Enter a whole number (−1 = unlimited).'); return; }
    applyLimit(row, n < 0 ? -1 : n);
  };

  const resetToPlan = (row: AIUsageRow) => applyLimit(row, null);

  const rows = (data?.byUser ?? []).filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.planSlug.toLowerCase().includes(q);
  });

  const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
  const fmtInt = (n: number) => n.toLocaleString();
  const fmtLimit = (v: number | null): string => v == null ? '—' : v < 0 ? '∞' : fmtInt(v);
  const fmtDate = (iso: string | null): string => {
    if (!iso) return 'never';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ', ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const card = (label: string, value: string, sub: string, icon: React.ReactNode) => (
    <div style={{ flex: 1, minWidth: 180, background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-dim)' }}>{icon}<span>{label}</span></div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-main)', marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{sub}</div>
    </div>
  );

  // One customer's used/limit cell, colored by how close they are to the limit.
  const usageCell = (r: AIUsageRow) => {
    const eff = effectiveLimit(r);
    if (eff != null && eff < 0) return <span style={{ color: 'var(--text-muted)' }}>{fmtInt(r.requests)} / ∞</span>;
    if (eff === 0) return <span style={{ fontSize: 10.5, background: 'rgba(239,68,68,0.15)', color: 'var(--danger-text)', borderRadius: 4, padding: '1px 6px' }}>suspended</span>;
    if (eff == null) return <span style={{ color: 'var(--text-dim)' }}>{fmtInt(r.requests)}</span>;
    const over = r.requests >= eff;
    const near = !over && r.requests >= eff * 0.8;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: over ? 'var(--danger-text)' : near ? '#f59e0b' : 'var(--text-main)' }}>
        {fmtInt(r.requests)} / {fmtInt(eff)}
        {over && <span style={{ fontSize: 10.5, background: 'rgba(239,68,68,0.15)', color: 'var(--danger-text)', borderRadius: 4, padding: '1px 6px' }}>reached</span>}
      </span>
    );
  };

  const detailLine = (k: string, v: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-dim)' }}>{k}</span>
      <span style={{ color: 'var(--text-main)', textAlign: 'right' }}>{v}</span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Sparkles size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Iris AI Usage</h2>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)', maxWidth: '680px' }}>
            Iris credits used this month (1 credit = one question). Each customer draws on their plan's allowance; open a customer to suspend, raise, or reset their limit. A custom limit overrides the plan; reset falls back to it.
          </p>
        </div>
        <button onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)' }}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      {/* Month totals */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {card('Questions this month', fmtInt(data?.requests ?? 0), 'answered by Iris', <MessageSquare size={13} />)}
        {card('Cost this month', fmtUsd(data?.costUsd ?? 0), 'Claude (Bedrock) spend', <DollarSign size={13} />)}
        {card('Tokens this month', fmtInt((data?.inputTokens ?? 0) + (data?.outputTokens ?? 0)), `${fmtInt(data?.cacheReadTokens ?? 0)} cached`, <Gauge size={13} />)}
        {card('Active customers', fmtInt((data?.byUser ?? []).filter((r) => r.requests > 0).length), 'asked at least once', <Sparkles size={13} />)}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '14px', maxWidth: '360px' }}>
        <Search size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-dim)' }} />
        <input type="text" className="input" placeholder="Search customer, email or plan…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: '100%', paddingLeft: '30px', fontSize: '13px' }} />
      </div>

      {/* Per-customer table */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={20} className="spin" /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No AI usage this month{search ? ' matching your search' : ''}.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-dim)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  <th style={{ padding: '10px 14px' }}>Customer</th>
                  <th style={{ padding: '10px 14px' }}>Plan</th>
                  <th style={{ padding: '10px 14px' }}>Used / limit</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Cost</th>
                  <th style={{ padding: '10px 14px' }}>Last asked</th>
                  <th style={{ padding: '10px 14px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ color: 'var(--text-main)' }}>{r.name || '—'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{r.email}</div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ color: 'var(--text-main)' }}>{r.planName || r.planSlug || '—'}</span>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        allowance {fmtLimit(r.planCredits)}{r.monthlyCap != null ? ` · override ${fmtLimit(r.monthlyCap)}` : ''}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{usageCell(r)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>{fmtUsd(r.costUsd)}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(r.lastUsedAt)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setSelected(r)}>
                        <Settings2 size={13} /> <span>Manage</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manage / detail modal */}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? (selected.name || selected.email) : 'Customer'}
        subtitle={selected ? selected.email : ''}
      >
        {selected && (() => {
          const eff = effectiveLimit(selected);
          const remaining = eff == null || eff < 0 ? null : Math.max(0, eff - selected.requests);
          return (
            <div>
              <div style={{ marginBottom: 16 }}>
                {detailLine('Plan', <>{selected.planName || selected.planSlug || '—'} <code style={{ fontSize: 11, color: 'var(--primary-light)' }}>{selected.planSlug}</code></>)}
                {detailLine('Plan allowance / month', fmtLimit(selected.planCredits) + (selected.planCredits === 0 ? ' (Iris off on plan)' : ''))}
                {detailLine('Custom override', selected.monthlyCap == null ? 'none — uses plan' : fmtLimit(selected.monthlyCap))}
                {detailLine('Effective limit', eff === 0 ? 'suspended' : fmtLimit(eff))}
                {detailLine('Used this month', `${fmtInt(selected.requests)} credit${selected.requests === 1 ? '' : 's'}`)}
                {detailLine('Remaining', remaining == null ? (eff != null && eff < 0 ? 'unlimited' : '—') : fmtInt(remaining))}
                {detailLine('Cost this month', fmtUsd(selected.costUsd))}
                {detailLine('Tokens (in / out / cached)', `${fmtInt(selected.inputTokens)} / ${fmtInt(selected.outputTokens)} / ${fmtInt(selected.cacheReadTokens)}`)}
                {detailLine('Last asked', fmtDate(selected.lastUsedAt))}
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Controls</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button className="btn btn-danger btn-sm" disabled={busy || eff === 0} onClick={() => suspend(selected)}>
                  {busy ? <Loader2 size={13} className="spin" /> : <Ban size={13} />} <span>Suspend Iris</span>
                </button>
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => setCustomLimit(selected)}>
                  <SlidersHorizontal size={13} /> <span>Set custom limit</span>
                </button>
                <button className="btn btn-secondary btn-sm" disabled={busy || selected.monthlyCap == null} onClick={() => resetToPlan(selected)}>
                  <RotateCcw size={13} /> <span>Reset to plan</span>
                </button>
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 10 }}>
                Suspend sets the limit to 0. A custom limit overrides the plan (−1 = unlimited). Reset removes the override so the plan allowance applies again. To change a whole plan's allowance, edit the plan in Service Plans.
              </p>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};
